import { useEffect, useMemo, useRef, useState } from "react";

/* Tempo — ARENA edition. A racing clock that turns red under pressure, combo
   flames, screen-edge flash on hit/miss. Calls onFinish({correct,total}). */
export default function Tempo({ module, accent = "#f59e0b", onFinish }) {
  const questions = useMemo(() => (module.questions || []).map((q) => {
    const order = q.options.map((_, i) => i).sort(() => Math.random() - 0.5);
    return { q: q.q, opts: order.map((i) => q.options[i]), answer: order.indexOf(q.answer) };
  }), [module.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const TOTAL = Math.max(20, questions.length * 7);
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [flash, setFlash] = useState(null);
  const [left, setLeft] = useState(TOTAL);
  const done = useRef(false);
  const scoreRef = useRef(0); scoreRef.current = score;

  useEffect(() => {
    const start = performance.now();
    const id = setInterval(() => {
      const rem = Math.max(0, TOTAL - (performance.now() - start) / 1000);
      setLeft(rem);
      if (rem <= 0 && !done.current) { done.current = true; clearInterval(id); onFinish({ correct: scoreRef.current, total: questions.length }); }
    }, 100);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const q = questions[idx];
  const advance = () => {
    if (done.current) return;
    if (idx + 1 < questions.length) { setIdx((i) => i + 1); setFlash(null); }
    else { done.current = true; onFinish({ correct: scoreRef.current, total: questions.length }); }
  };
  const choose = (i) => {
    if (flash) return;
    const ok = i === q.answer;
    if (ok) { setScore((s) => s + 1); setCombo((c) => c + 1); } else setCombo(0);
    setFlash(ok ? "ok" : "no");
    setTimeout(advance, 320);
  };

  const pct = (left / TOTAL) * 100;
  const urgent = left <= TOTAL * 0.25;

  return (
    <div className="mx-auto w-full max-w-3xl">
      {/* clock HUD */}
      <div className="mb-2 flex items-center justify-between">
        <span className={`rounded-full px-3 py-1.5 text-sm font-black tabular-nums ${urgent ? "animate-pulse" : ""}`}
          style={{ background: urgent ? "rgba(239,68,68,0.16)" : `${accent}18`, color: urgent ? "#f87171" : accent, border: `1px solid ${urgent ? "rgba(239,68,68,0.45)" : `${accent}45`}` }}>
          ⏱ {left.toFixed(1)}s
        </span>
        <span className="text-xs font-black text-white/50">{idx + 1}/{questions.length}</span>
        {combo >= 2
          ? <span className="animate-pulse rounded-full bg-gold/15 px-3 py-1.5 text-sm font-black text-gold" style={{ border: "1px solid rgba(251,191,36,0.4)" }}>🔥 {combo}× COMBO</span>
          : <span className="w-20" />}
      </div>
      <div className="h-2.5 w-full overflow-hidden rounded-full bg-white/10">
        <div className="h-full rounded-full transition-[width] duration-100"
          style={{ width: `${pct}%`, background: urgent ? "linear-gradient(90deg,#ef4444,#f97316)" : `linear-gradient(90deg, ${accent}, #ec4899)`, boxShadow: urgent ? "0 0 14px rgba(239,68,68,0.7)" : `0 0 12px ${accent}60` }} />
      </div>

      <div key={idx} className="mt-5 rounded-[2rem] border p-6 backdrop-blur-xl transition-colors sm:p-9"
        style={{
          animation: "arena-in 0.25s ease both",
          borderColor: flash === "ok" ? "rgba(34,197,94,0.6)" : flash === "no" ? "rgba(239,68,68,0.6)" : `${accent}33`,
          background: flash === "ok" ? "rgba(34,197,94,0.08)" : flash === "no" ? "rgba(239,68,68,0.08)" : `linear-gradient(165deg, ${accent}0e 0%, rgba(8,6,26,0.85) 55%)`,
          boxShadow: flash === "ok" ? "0 0 40px rgba(34,197,94,0.25)" : flash === "no" ? "0 0 40px rgba(239,68,68,0.25)" : `0 0 44px ${accent}1c, 0 20px 50px rgba(0,0,0,0.45)`,
        }}>
        <h3 className="text-xl font-black leading-snug sm:text-3xl">{q.q}</h3>
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          {q.opts.map((opt, i) => (
            <button key={i} onClick={() => choose(i)} disabled={!!flash}
              className="rounded-2xl border border-white/12 bg-white/[0.04] px-5 py-4.5 text-left text-base font-black text-white transition-all hover:-translate-y-0.5 hover:bg-white/[0.08] active:scale-[0.97] disabled:opacity-60 sm:text-lg"
              style={{ padding: "1.1rem 1.25rem" }}
              onMouseEnter={(e) => { if (!flash) e.currentTarget.style.borderColor = `${accent}80`; }}
              onMouseLeave={(e) => { if (!flash) e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)"; }}>
              {opt}
            </button>
          ))}
        </div>
      </div>
      <p className="mt-4 text-center text-xs font-bold text-white/40">Schnell tippen! Jede richtige Antwort zählt, bevor die Zeit abläuft.</p>
    </div>
  );
}
